/**
 * The two card-preference pickers (card-preferences design § Controls): sort direction and
 * rank-label style, each a `segmented-picker.js` control over its own localStorage-backed pref
 * (`sort-order.js` / `rank-style.js`). `buildRankStylePicker` centralizes its own DE-only
 * visibility gate (English already renders J/Q either way, so the choice doesn't exist there) —
 * callers append the section only when it's non-null.
 *
 * Option icons are mini card renderings (card-preference-pickers-redesign brief § 1) — not a
 * member of the icons/ stroke-icon families: what these two prefs change *is* the card, so the
 * honest vocabulary is the card system. They visually rhyme with the shipped `.ri-card` mini card
 * tile (skat.css § round inspection) — same idea, same reason for the stripe (an overlapped fan
 * still identifies a card off its sliver) — but built in SVG from JS rather than reusing those CSS
 * classes, so it reads `SUIT_COLORS` from `cards/cards.js` instead of adding a third hardcoded
 * copy of the suit hexes. Geometry ported verbatim from `scratch/pick.mjs` (worktree-local,
 * gitignored), a live prototype tuned against the running server — not re-derived by eye.
 *
 * Both option tiers are icon-only (label removal, brief § 2 — show-don't-tell, the card faces are
 * the choice): `prefSortOrder`/`prefRankStyle` are repurposed from the retired section labels into
 * each option's `aria-label`/`data-tooltip` text (segmented-picker.js's paired convention) — "still
 * the right words," just no longer painted.
 */
import { t, getActiveLanguage } from "./i18n.js";
import {
  createSegmentedPicker,
  pressSegmentedPicker,
} from "./segmented-picker.js";
import { sortDescending, setSortDescending } from "./sort-order.js";
import { rankStyle, setRankStyle } from "./rank-style.js";
import { SUIT_COLORS } from "./cards/cards.js";
import { addFaceMotif } from "./cards/card-motifs.js";
import { VELLUM } from "./cards/card-invert.js";

const SVG_NS = "http://www.w3.org/2000/svg";

function svgEl(tag, attrs) {
  const node = document.createElementNS(SVG_NS, tag);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

// The real card's local frame (250×350) — brief § 1's ported geometry. -6/-6/262/362 gives the
// vellum stroke's own half-width (7/2 ≈ 3.5, rounded up) a hair of room so it isn't clipped.
const MINI_CARD_VIEWBOX = "-6 -6 262 362";
const VELLUM_STROKE = "rgba(0,0,0,0.28)";

/** The vellum face + suit-coloured left stripe — brief § 1's two base layers, always present. */
function appendVellumBase(svg, color) {
  svg.appendChild(
    svgEl("rect", {
      x: 0,
      y: 0,
      width: 250,
      height: 350,
      rx: 26,
      fill: VELLUM,
      stroke: VELLUM_STROKE,
      "stroke-width": 7,
    }),
  );
  svg.appendChild(
    svgEl("rect", { x: 6, y: 6, width: 22, height: 338, rx: 11, fill: color }),
  );
}

/** The corner rank glyph — brief § 1's top-left mark, suit-coloured Satoshi 700. */
function appendRankGlyph(svg, rankMark, color) {
  const text = svgEl("text", {
    x: 80,
    y: 108,
    "font-family": "'Satoshi', system-ui, sans-serif",
    "font-weight": 700,
    "font-size": 118,
    fill: color,
    "text-anchor": "middle",
  });
  text.textContent = rankMark;
  svg.appendChild(text);
}

/**
 * One mini card face: vellum rect + suit-coloured left stripe + suit-coloured rank glyph, plus an
 * optional centre motif (`addFaceMotif`, `cards/card-motifs.js`). No width/height attribute on the
 * svg — CSS drives sizing (`.pref-mini-card { height: ...; width: auto }`), and the element's own
 * viewBox aspect ratio supplies the `auto` side, so the 262/362 ratio lives in exactly one place.
 */
function miniCard(suit, rankMark, motifRank) {
  const color = SUIT_COLORS[suit];
  const svg = svgEl("svg", { viewBox: MINI_CARD_VIEWBOX });
  svg.classList.add("pref-mini-card");
  appendVellumBase(svg, color);
  if (motifRank != null) {
    const g = svgEl("g", {
      transform: "translate(132,190) scale(0.66) translate(-125,-175)",
    });
    svg.appendChild(g);
    addFaceMotif(g, motifRank, color);
  }
  appendRankGlyph(svg, rankMark, color);
  return svg;
}

// The real display order: rules/CardComparator sorts non-trump suits ascending D < H < S (clubs
// is the fourth suit, reserved for the rank picker below so the two pickers use all four between
// them). Ranks 7·8·9 (settled with Andre) read as ordering with no prior knowledge of Skat's rank
// power — 7·K·A was considered and rejected, see the brief.
const SORT_FAN_ASCENDING = [
  { suit: "diamonds", rank: "7" },
  { suit: "hearts", rank: "8" },
  { suit: "spades", rank: "9" },
];

/** The sort-direction fan: three overlapped mini cards, no motifs — an overlapped fan only shows
 *  slivers, so the stripe + corner index is the whole read (the `.ri-card` rationale). Descending
 *  is the exact reverse of the ascending array on both axes together (suit AND rank swap as one
 *  unit, not independently) — exactly what `decode-wire-state.js` does to the decoded card array. */
function buildSortFanIcon(descending) {
  const cards = descending
    ? [...SORT_FAN_ASCENDING].reverse()
    : SORT_FAN_ASCENDING;
  const fan = document.createElement("div");
  fan.className = descending
    ? "pref-sort-fan pref-sort-fan--desc"
    : "pref-sort-fan pref-sort-fan--asc";
  for (const { suit, rank } of cards)
    fan.appendChild(miniCard(suit, rank, null));
  return fan;
}

// Clubs — the fourth suit, so the two pickers use all four between them. The motif is IDENTICAL
// across german/french (same two cards, jack then queen); only the corner letter changes, which is
// the whole point — rankStyle picks a NAME for the same cards, not different cards.
//
// Letters come from the same rankJack/rankQueen(French) i18n keys cards.js#rankDisplay resolves —
// not a hardcoded second copy of "U"/"O"/"B"/"D". rankDisplay itself can't be reused directly (it
// always resolves the CURRENT rankStyle() preference, so it can only ever return one variant at a
// time), but the letters it would resolve to are still sourced from t() here, same as there.
const RANK_PAIR_KEYS = {
  german: ["rankJack", "rankQueen"],
  french: ["rankJackFrench", "rankQueenFrench"],
};
const RANK_PAIR_MOTIFS = ["jack", "queen"];

function buildRankPairIcon(style) {
  const [jackKey, queenKey] = RANK_PAIR_KEYS[style];
  const pair = document.createElement("div");
  pair.className = "pref-rank-pair";
  pair.appendChild(miniCard("clubs", t(jackKey), RANK_PAIR_MOTIFS[0]));
  pair.appendChild(miniCard("clubs", t(queenKey), RANK_PAIR_MOTIFS[1]));
  return pair;
}

/** The sort-direction picker: ascending (today's order) vs descending. No `onAfterChange` on the
 *  start screen — nothing card-shaped renders there, so the next fetch/deal just decodes with the
 *  new value; the profile overlay's Karten section passes its own context-dependent hook
 *  (game-start/game-start-profile-sections.js). `label` is optional and forwarded to the segmented
 *  picker's own label tier: the start-screen mount stays labelless (show-don't-tell, brief § 2),
 *  the profile overlay's stacked pickers name themselves (profile-overlay-and-menu-slim design —
 *  readability wins in a settings surface). */
export function buildSortOrderPicker({ label, onAfterChange } = {}) {
  return createSegmentedPicker({
    label,
    options: [
      {
        value: "asc",
        icon: buildSortFanIcon(false),
        ariaLabel: `${t("prefSortOrder")}: 7 · 8 · 9`,
      },
      {
        value: "desc",
        icon: buildSortFanIcon(true),
        ariaLabel: `${t("prefSortOrder")}: 9 · 8 · 7`,
      },
    ],
    active: sortDescending() ? "desc" : "asc",
    onSelect: (value) => {
      setSortDescending(value === "desc");
      onAfterChange?.();
    },
  });
}

/** The rank-style picker: German (U/O) vs French (B/D) — `null` under any language but German,
 *  since the choice doesn't exist in English. `label` as above. */
export function buildRankStylePicker({ label, onAfterChange } = {}) {
  if (getActiveLanguage() !== "de") return null;
  return createSegmentedPicker({
    label,
    options: [
      {
        value: "german",
        icon: buildRankPairIcon("german"),
        ariaLabel: `${t("prefRankStyle")}: ${t("rankJack")} · ${t("rankQueen")}`,
      },
      {
        value: "french",
        icon: buildRankPairIcon("french"),
        ariaLabel: `${t("prefRankStyle")}: ${t("rankJackFrench")} · ${t("rankQueenFrench")}`,
      },
    ],
    active: rankStyle(),
    onSelect: (value) => {
      setRankStyle(value);
      onAfterChange?.();
    },
  });
}

/**
 * Re-presses an already-built sort/rank picker pair to the CURRENT localStorage-backed pref
 * values (rulings round 1 § Item 2, scratch/rulings-round-1.md — the game-start inline pickers
 * going stale when the pref changes via the start-screen menu's own copy of the same two
 * controls). `rankSection` is optional — a caller whose pair has no rank picker mounted (English)
 * passes only the sort section. Takes the built `.segmented-picker` sections directly rather than
 * re-deriving which is which — the caller (game-start-control-bank.js#refreshPickersGroup) already
 * knows its own mount order.
 */
export function refreshPickerSelections(sortSection, rankSection) {
  pressSegmentedPicker(sortSection, sortDescending() ? "desc" : "asc");
  if (rankSection != null) pressSegmentedPicker(rankSection, rankStyle());
}
